/**
 * A problem's title in plain words for the System Pulse headline (V7: "38 residents exposed
 * right now · Shady Acres · Atlas silent"). The monitor names a service problem by its metric
 * ("service:requests:latency-p95 · SLO burn (critical): service/latency-p95; target 99%");
 * the headline says what that means for people. A title it does not recognise is kept as is.
 */
const SIGNAL_WORDS: Array<[RegExp, string]> = [
  [/latency|p95|p99|slow/i, 'answers slower than the target'],
  [/error|fail|5xx/i, 'requests failing more than the target allows'],
  [/avail|uptime|up\b/i, 'not answering as often as the target'],
];

/** Aithora's own platform checks, by the name the monitor gives them. */
const CHECK_WORDS: Record<string, string> = {
  'llm-providers': 'AI answers',
  database: 'database',
  db: 'database',
  redis: 'cache',
  'secret-manager': 'credential store',
  kms: 'credential store',
  storage: 'file storage',
  twilio: 'texts and calls',
  email: 'email',
};
const checkWords = (name: string) => CHECK_WORDS[name.toLowerCase()] ?? name.replace(/[-_]+/g, ' ');

export function plainProblemTitle(title: string): string {
  const slo = /^(?:[a-z0-9_-]+(?::[a-z0-9_.-]+)+ · )?SLO burn \((critical|warning)\): ([^;]+?)(?:; target ([\d.]+)%)?$/i.exec(title);
  if (slo) {
    const signal = slo[2];
    const words = SIGNAL_WORDS.find(([re]) => re.test(signal))?.[1] ?? 'below its target';
    return `Aithora · ${words}${slo[3] ? ` (${slo[3]}% target)` : ''}`;
  }
  // "llm-providers · Platform check DOWN: llm-providers": one of Aithora's own checks.
  const check = /^(?:\S+ · )?Platform check DOWN: (\S+)$/.exec(title);
  if (check) return `Aithora · ${checkWords(check[1])} down`;
  // "canary · Canary FAILED (2× in a row)": the test conversation Aithora runs on itself.
  const canary = /^(?:\S+ · )?(Care-path canary|Canary) FAILED \((\d+)× in a row\)$/.exec(title);
  if (canary) return `Aithora · ${canary[1] === 'Canary' ? 'test conversation' : 'care-path test'} failed${Number(canary[2]) > 1 ? ` ${canary[2]} times in a row` : ''}`;
  // "service:requests:x · ...": the metric name is not a place.
  const tagged = /^[a-z0-9_-]+(?::[a-z0-9_.-]+)+ · (.+)$/i.exec(title);
  return tagged ? `Aithora · ${tagged[1]}` : title;
}
